import {
  authCardSurfaceSx,
  authDescriptionSlate,
  authHeadingSlate,
  authTopBarBorder,
  palettePrimaryMain,
  surfaceAuthCanvas,
} from '@/theme/designTokens'
import {
  SIGNUP_VERIFY_BUTTON_DISABLED_BG,
  signupVerifyCardSx,
  signupVerifyContentColumnSx,
  signupVerifyTitleBlockSx,
} from '@/features/auth/styles/signupVerifyStyles'
import { inputSx, labelSx } from '@/features/auth/styles/authFormStyles'

export {
  signupVerifyHeadingSx as instrumentVerifyHeadingSx,
  signupVerifySubtitleSx as instrumentVerifySubtitleSx,
  signupVerifyTitleBlockSx as instrumentOtpTitleBlockSx,
  signupVerifyOtpLabelSx as instrumentVerifyOtpLabelSx,
  signupVerifyContentColumnSx as instrumentVerifyContentColumnSx,
  signupVerifyPrimaryButtonSx as instrumentVerifyPrimaryButtonSx,
  signupVerifyResendLinkSx as instrumentVerifyResendLinkSx,
} from '@/features/auth/styles/signupVerifyStyles'

export const INSTRUMENT_VERIFY_CARD_WIDTH_PX = 480
/** Figma 136:4371 — survey-first Enter Your Mobile Number card */
export const SURVEY_FIRST_VERIFY_CARD_WIDTH_PX = 574
/** Figma 136:4378 / 136:4384 — field + Send OTP */
export const SURVEY_FIRST_OTP_CONTENT_WIDTH_PX = 416
/** Figma 136:4373 — title inset 96px each side on 574 */
export const SURVEY_FIRST_TITLE_WIDTH_PX = 382
/** Survey-first / Create Account wordmark — larger than the 57px Figma lockup */
export const SURVEY_FIRST_LOGO_HEIGHT_PX = 96
export const SURVEY_FIRST_LOGO_MAX_WIDTH_PX = 280

export const instrumentVerifyCardSx = {
  ...signupVerifyCardSx,
  width: '100%',
  maxWidth: INSTRUMENT_VERIFY_CARD_WIDTH_PX,
} as const

/** Figma 136:4371 — 574 card, 26/79 padding, form column 416 */
export const surveyFirstVerifyCardSx = {
  ...instrumentVerifyCardSx,
  maxWidth: SURVEY_FIRST_VERIFY_CARD_WIDTH_PX,
  p: { xs: 3, sm: '26px 79px 32px' },
  gap: 0,
  overflow: 'visible',
} as const

export const surveyFirstLogoBlockSx = {
  height: { xs: 80, sm: SURVEY_FIRST_LOGO_HEIGHT_PX },
  minHeight: { xs: 80, sm: SURVEY_FIRST_LOGO_HEIGHT_PX },
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  mb: { xs: 2, sm: '28px' },
} as const

export const surveyFirstLogoImgSx = {
  width: 'auto',
  height: { xs: 80, sm: SURVEY_FIRST_LOGO_HEIGHT_PX },
  maxWidth: { xs: '100%', sm: SURVEY_FIRST_LOGO_MAX_WIDTH_PX },
  objectFit: 'contain',
  objectPosition: 'center',
  display: 'block',
  flexShrink: 0,
} as const

export const surveyFirstOtpContentColumnSx = {
  ...signupVerifyContentColumnSx,
  maxWidth: SURVEY_FIRST_OTP_CONTENT_WIDTH_PX,
} as const

export const surveyFirstFormColumnSx = {
  ...surveyFirstOtpContentColumnSx,
  mt: 0,
} as const

/** Figma 75:1365 — heading to subtitle gap 12px */
export const instrumentPhoneTitleBlockSx = {
  ...signupVerifyTitleBlockSx,
  gap: '12px',
} as const

/** Figma 136:4373 — 12px title gap, 46px below stepper, 382px wrap width */
export const surveyFirstTitleBlockSx = {
  ...instrumentPhoneTitleBlockSx,
  maxWidth: { xs: '100%', sm: SURVEY_FIRST_TITLE_WIDTH_PX },
  width: '100%',
  mx: 'auto',
  mt: { xs: 3, sm: '46px' },
  mb: { xs: 2, sm: '19px' },
} as const

/** Figma 136:4375 — Poppins SemiBold 32/40, 63px frame so the title wraps */
export const surveyFirstHeadingSx = {
  fontFamily: 'Poppins, Inter, sans-serif',
  fontWeight: 600,
  fontSize: { xs: '28px', sm: '32px' },
  lineHeight: { xs: '36px', sm: '40px' },
  letterSpacing: '0.4063px',
  color: authHeadingSlate,
  textAlign: 'center',
  m: 0,
  width: '100%',
  minHeight: { xs: 'auto', sm: 63 },
} as const

export const surveyFirstSubtitleSx = {
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  color: authDescriptionSlate,
  textAlign: 'center',
  m: 0,
  minHeight: 48,
  width: '100%',
} as const

export const surveyFirstBackToLoginLinkSx = {
  mt: 0,
  mb: 0,
  whiteSpace: 'nowrap',
} as const

export const channelSectionTitleSx = {
  fontWeight: 600,
  fontSize: '18px',
  lineHeight: '28px',
  color: authHeadingSlate,
  textAlign: 'center',
  m: 0,
  width: '100%',
} as const

export const channelGridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '16px',
  width: '100%',
  pt: '8px',
} as const

export const channelCardBaseSx = {
  borderRadius: '12px',
  minHeight: 96,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  cursor: 'pointer',
  position: 'relative',
  border: '1px solid #bfc7d2',
  bgcolor: '#ffffff',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s, background-color 0.15s',
  '&:focus-visible': {
    outline: `2px solid ${palettePrimaryMain}`,
    outlineOffset: 2,
  },
} as const

export const channelCardSelectedSx = {
  border: `2px solid ${palettePrimaryMain}`,
  bgcolor: '#eff4ff',
} as const

export const channelCardDisabledSx = {
  opacity: 0.45,
  cursor: 'not-allowed',
  bgcolor: '#f5f7fa',
  '&:hover': { border: '1px solid #bfc7d2' },
} as const

export const channelCardLabelSx = {
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '16px',
  color: '#0b1c30',
  m: 0,
} as const

export const channelCardLabelSelectedSx = {
  color: palettePrimaryMain,
} as const

export const channelCheckmarkSx = {
  position: 'absolute',
  top: 8,
  right: 8,
  width: 15,
  height: 15,
  borderRadius: '50%',
  bgcolor: palettePrimaryMain,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#ffffff',
  fontSize: 10,
  fontWeight: 700,
} as const

/** Gap between contact label and input (flex gap — label `mb` alone is unreliable on inline labels). */
export const INSTRUMENT_CONTACT_LABEL_INPUT_GAP_PX = 8

/** Space above Email Address / Mobile Number block below channel cards. */
export const instrumentContactFieldSx = {
  width: '100%',
  mt: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: `${INSTRUMENT_CONTACT_LABEL_INPUT_GAP_PX}px`,
} as const

export const instrumentContactLabelSx = {
  ...labelSx,
  display: 'block',
  mb: 0,
} as const

export const instrumentContactInputSx = {
  ...inputSx,
  mt: 0,
  mb: 0,
} as const

/** Pull link up toward card bottom — card padding is 49px; Figma tightens footer inset. */
export const BACK_TO_LOGIN_LINK_NEGATIVE_MB_PX = 24

export const backToLoginLinkSx = {
  fontSize: '14px',
  lineHeight: '20px',
  textAlign: 'center',
  width: '100%',
  mt: 2,
  mx: 0,
  mb: `-${BACK_TO_LOGIN_LINK_NEGATIVE_MB_PX}px`,
  color: authDescriptionSlate,
  '& a': {
    color: 'inherit',
    textDecoration: 'none',
    fontWeight: 400,
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
  },
} as const

export const backToLoginEmphasisSx = {
  fontWeight: 700,
  color: palettePrimaryMain,
} as const

/** Figma 4950:6889 — consent canvas (672px) */
export const CONSENT_PAGE_MAX_WIDTH_PX = 672

export const consentPageRootSx = {
  width: '100%',
  maxWidth: CONSENT_PAGE_MAX_WIDTH_PX,
  mx: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '32px',
  alignItems: 'stretch',
  boxSizing: 'border-box',
} as const

export const consentBeforeHeadingSx = {
  fontWeight: 700,
  fontSize: { xs: '24px', md: '32px' },
  lineHeight: { xs: '32px', md: '40px' },
  letterSpacing: '0.4063px',
  color: authHeadingSlate,
  textAlign: 'center',
  m: 0,
} as const

export const consentBeforeSubtitleSx = {
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '24px',
  color: '#3f4850',
  textAlign: 'center',
  m: 0,
} as const

export const consentCardSx = {
  ...authCardSurfaceSx,
  width: '100%',
  border: '1px solid #bfc7d2',
  borderRadius: '8px',
  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  height: 508,
} as const

export const consentScrollRegionSx = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  px: '32px',
  pt: '32px',
  pb: '16px',
} as const

export const consentPolicyTitleSx = {
  fontWeight: 600,
  fontSize: '18px',
  lineHeight: '28px',
  color: authHeadingSlate,
  textAlign: 'center',
  m: 0,
  mb: '14px',
} as const

export const consentBodyTextSx = {
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  color: '#3f4850',
  m: 0,
  mb: '12px',
  whiteSpace: 'pre-wrap',
} as const

export const consentSectionHeadingSx = {
  fontWeight: 500,
  fontSize: '20px',
  lineHeight: '28px',
  color: '#0b1c30',
  m: 0,
  mt: '16px',
  mb: '8px',
} as const

export const consentLastUpdatedSx = {
  fontWeight: 500,
  fontSize: '12px',
  lineHeight: '16px',
  letterSpacing: '0.12px',
  color: '#94a3b8',
  m: 0,
  mt: '16px',
} as const

export const consentFooterSx = {
  borderTop: `1px solid ${authTopBarBorder}`,
  bgcolor: '#ffffff',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
  px: '32px',
  pt: '25px',
  pb: '24px',
  flexShrink: 0,
} as const

export const consentAcceptButtonSx = {
  borderRadius: '8px',
  py: '16px',
  px: '24px',
  minHeight: 56,
  width: '100%',
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  letterSpacing: '0.15px',
  textTransform: 'none',
  boxShadow: 'none',
  color: '#f8fafc',
  '&.Mui-disabled': {
    bgcolor: SIGNUP_VERIFY_BUTTON_DISABLED_BG,
    color: '#f8fafc',
  },
  '&:not(.Mui-disabled)': {
    bgcolor: palettePrimaryMain,
    '&:hover': { bgcolor: palettePrimaryMain, opacity: 0.92 },
  },
} as const

export const consentDeclineButtonSx = {
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '12px',
  lineHeight: '16px',
  letterSpacing: '0.12px',
  color: authDescriptionSlate,
  minHeight: 32,
  p: '8px',
  '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
} as const

/** Figma 4950:6971 — decline footer inner stack (inside policy card footer). */
export const consentDeclinedPanelSx = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
} as const

export const consentDeclinedMessageSx = {
  fontWeight: 500,
  fontSize: '12px',
  lineHeight: '16px',
  letterSpacing: '0.12px',
  color: '#0f172a',
  textAlign: 'center',
  maxWidth: 482,
  m: 0,
} as const

export const consentRetryButtonSx = {
  borderRadius: '8px',
  py: '16px',
  px: '24px',
  minHeight: 56,
  width: '100%',
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  letterSpacing: '0.15px',
  textTransform: 'none',
  boxShadow: 'none',
  border: `1px solid ${palettePrimaryMain}`,
  color: palettePrimaryMain,
  bgcolor: 'transparent',
  '&:hover': {
    bgcolor: '#eff4ff',
    borderColor: palettePrimaryMain,
  },
} as const

export const instrumentOnboardingCanvasSx = {
  bgcolor: surfaceAuthCanvas,
} as const
